// Модели Claude: каталог из CLI и выбор владельца по областям.
// Каталог собирается без траты токенов: list_models + по каждой модели set_model / get_settings /
// get_context_usage в коротком процессе CLI (docs/CLI-PROTOCOL.md §5). Новые модели появляются сами,
// но платформа никогда не переключается на них без выбора владельца: храним явные id, не алиасы.

/** Модель по умолчанию — сильнейшая (решение владельца 24.09.2026). */
export const DEFAULT_MODEL = 'claude-opus-5-5';
/** Глубина размышлений по умолчанию (решение владельца 24.09.2026). Без флага CLI ставит Opus «medium». */
export const DEFAULT_EFFORT = 'high';

/** Уровни, которые знает CLI 2.1.280. Новые уровни из CLI тоже принимаются — показываем их как есть. */
export const EFFORT_LABEL: Record<string, string> = {
  low: 'Низкая',
  medium: 'Средняя',
  high: 'Высокая',
  xhigh: 'Очень высокая',
  max: 'Максимальная',
};

export function effortLabel(effort: string): string {
  return EFFORT_LABEL[effort] ?? effort;
}

export interface ModelInfo {
  /** Явный id для --model. */
  id: string;
  /** «Opus 5.5». */
  label: string;
  /** Короткое описание по-русски (или текст CLI, если перевода ещё нет). */
  description: string;
  /** Размер контекста в токенах, если CLI его сообщил. */
  contextWindow: number | null;
  /** Уровни глубины размышлений; пусто — модель не даёт выбора. */
  efforts: string[];
  /** Уровень, который CLI ставит сам, если флаг --effort не передан. */
  cliDefaultEffort: string | null;
  /** Когда платформа впервые увидела модель (null — была с первого запроса). */
  firstSeenAt: string | null;
}

export interface ModelCatalog {
  models: ModelInfo[];
  fetchedAt: string;
  cliVersion: string | null;
}

// ─── сырой ответ CLI (собирает runner) ──────────────────────────────────
export interface CliModelEntry {
  value: string;
  resolvedModel?: string;
  displayName?: string;
  description?: string;
  supportsEffort?: boolean;
  supportedEffortLevels?: string[];
}

export interface ModelProbe {
  model: string;
  ok: boolean;
  error?: string;
  effort?: string | null;
  contextWindow?: number | null;
}

export interface CliModelsRaw {
  cliVersion: string | null;
  list: { models?: CliModelEntry[] } | CliModelEntry[];
  probes: ModelProbe[];
}

function entries(raw: CliModelsRaw['list']): CliModelEntry[] {
  const list = Array.isArray(raw) ? raw : (raw?.models ?? []);
  return list.filter((m): m is CliModelEntry => typeof m?.value === 'string');
}

const isExplicitId = (s: string) => /^claude-/i.test(s);
const baseId = (s: string) => s.replace(/\[[^\]]*\]$/, '');

/** Какие явные id проверить у CLI: для каждой записи — сам id и его вариант без суффикса контекста. */
export function modelCandidates(list: CliModelsRaw['list']): string[] {
  const out: string[] = [];
  const add = (s: string | undefined) => {
    if (s && isExplicitId(s) && !out.includes(s)) out.push(s);
  };
  for (const e of entries(list)) {
    for (const id of [e.resolvedModel, e.value]) {
      if (!id || !isExplicitId(id)) continue;
      add(baseId(id));
      add(id);
    }
  }
  return out;
}

const FAMILY = /^claude-([a-z]+)-(\d+)(?:-(\d{1,2}))?(?=$|-\d{8}|\[)/i;

/** «claude-opus-5-5» → «Opus 5.5», «claude-haiku-4-5-20251001» → «Haiku 4.5». */
export function modelLabel(id: string): string {
  const m = FAMILY.exec(id);
  if (m?.[1] && m[2]) {
    const name = m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
    return `${name} ${m[2]}${m[3] ? `.${m[3]}` : ''}`;
  }
  if (/^[a-z]+$/i.test(id)) return id.charAt(0).toUpperCase() + id.slice(1).toLowerCase();
  return id;
}

/** Переводы описаний, которые пишет CLI. Незнакомое описание показываем как есть — лучше, чем выдумать. */
const DESCRIPTION_RU: Record<string, string> = {
  'best for everyday, complex tasks': 'Сильная модель для повседневной сложной работы',
  'most capable for your hardest and longest-running tasks':
    'Самая способная — для самых трудных и долгих задач',
  'efficient for routine tasks': 'Экономнее и быстрее — для рутинных задач',
  'fastest for quick answers': 'Самая быстрая — для коротких ответов',
};

function describeModel(raw: string | undefined): string {
  if (!raw) return '';
  const tail = raw.includes('·') ? (raw.split('·').pop() ?? raw) : raw;
  const t = tail.trim();
  return DESCRIPTION_RU[t.toLowerCase()] ?? t;
}

/**
 * Порядок в списке. Свой, а не как у CLI: «рекомендуемую» модель CLI выбирает по способу входа
 * (с токеном setup-token он не знает тариф и советует Sonnet), для владельца это не ориентир.
 * Незнакомые семейства — в конце, внутри семейства — сначала новые версии.
 */
const FAMILY_ORDER = ['opus', 'fable', 'sonnet', 'haiku'];

function sortKey(id: string): [number, number, number] {
  const m = FAMILY.exec(id);
  const rank = m?.[1] ? FAMILY_ORDER.indexOf(m[1].toLowerCase()) : -1;
  return [rank < 0 ? FAMILY_ORDER.length : rank, -Number(m?.[2] ?? 0), -Number(m?.[3] ?? 0)];
}

function byFamily(a: ModelInfo, b: ModelInfo): number {
  const x = sortKey(a.id);
  const y = sortKey(b.id);
  return x[0] - y[0] || x[1] - y[1] || x[2] - y[2];
}

/**
 * Каталог из ответа CLI. Одна модель может прийти под несколькими записями (алиас «default» и «opus[1m]»):
 * берём явный id с наибольшим контекстом, при равенстве — самый короткий (без суффикса).
 * Модели, которые CLI отверг в set_model, в каталог не попадают.
 */
export function buildCatalog(raw: CliModelsRaw, prev: ModelCatalog | null, now = new Date()): ModelCatalog {
  const probes = new Map(raw.probes.map((p) => [p.model, p]));
  const pick = (e: CliModelEntry): ModelProbe | null => {
    const ids = [e.resolvedModel, e.value]
      .filter((x): x is string => !!x && isExplicitId(x))
      .flatMap((x) => [baseId(x), x]);
    let best: ModelProbe | null = null;
    for (const id of ids) {
      const p = probes.get(id);
      if (!p?.ok) continue;
      const ctx = p.contextWindow ?? 0;
      const bestCtx = best?.contextWindow ?? 0;
      if (!best || ctx > bestCtx || (ctx === bestCtx && p.model.length < best.model.length)) best = p;
    }
    return best;
  };
  const list = entries(raw.list);
  const prevSeen = new Map(prev?.models.map((m) => [m.id, m.firstSeenAt]) ?? []);

  const models: ModelInfo[] = [];
  for (const e of list) {
    const p = pick(e);
    if (!p || models.some((m) => m.id === p.model)) continue;
    // Описание берём у записи, которая не алиас «default» (у неё displayName «Default (recommended)»)
    const src = e.value === 'default' ? (list.find((x) => x !== e && pick(x)?.model === p.model) ?? e) : e;
    models.push({
      id: p.model,
      label: modelLabel(p.model),
      description: describeModel(src.description),
      contextWindow: p.contextWindow ?? null,
      efforts: e.supportsEffort === false ? [] : (e.supportedEffortLevels ?? []),
      cliDefaultEffort: p.effort ?? null,
      firstSeenAt: prev
        ? prevSeen.has(p.model)
          ? (prevSeen.get(p.model) ?? null)
          : now.toISOString()
        : null,
    });
  }
  models.sort(byFamily);
  return { models, fetchedAt: now.toISOString(), cliVersion: raw.cliVersion };
}

/** «новая» — модель появилась в списке за последние 14 дней. */
export function isNewModel(m: ModelInfo, now = Date.now()): boolean {
  return m.firstSeenAt !== null && now - new Date(m.firstSeenAt).getTime() < 14 * 86_400_000;
}

/** «1M», «200K». */
export function contextLabel(tokens: number | null): string | null {
  if (!tokens) return null;
  if (tokens >= 1_000_000) return `${+(tokens / 1_000_000).toFixed(1)}M`;
  return `${Math.round(tokens / 1000)}K`;
}

// ─── выбор владельца ────────────────────────────────────────────────────
export type ModelScope = 'chat' | 'rounds' | 'creative';
export const MODEL_SCOPES: ModelScope[] = ['chat', 'rounds', 'creative'];

export interface ModelChoice {
  model: string;
  /** null — модель не даёт выбора глубины, флаг --effort не передаётся. */
  effort: string | null;
}

export interface ModelSettings {
  default: ModelChoice;
  /** Переопределения по областям; нет ключа — как по умолчанию. */
  scopes: Partial<Record<ModelScope, ModelChoice>>;
}

export const DEFAULT_MODEL_SETTINGS: ModelSettings = {
  default: { model: DEFAULT_MODEL, effort: DEFAULT_EFFORT },
  scopes: {},
};

export function resolveModel(s: ModelSettings, scope: ModelScope): ModelChoice {
  return s.scopes[scope] ?? s.default;
}

/**
 * Глубина для выбранной модели: сохраняем выбранную, если модель её поддерживает;
 * иначе — уровень по умолчанию владельца, затем уровень CLI. Модель без уровней — null.
 */
export function effortFor(model: ModelInfo | undefined, wanted: string | null): string | null {
  if (!model) return wanted;
  if (model.efforts.length === 0) return null;
  if (wanted && model.efforts.includes(wanted)) return wanted;
  if (model.efforts.includes(DEFAULT_EFFORT)) return DEFAULT_EFFORT;
  return model.cliDefaultEffort ?? model.efforts[0] ?? null;
}

/** Проверка выбора по каталогу: текст ошибки для человека или null. */
export function checkChoice(choice: ModelChoice, catalog: ModelCatalog | null): string | null {
  if (!catalog) return null; // каталога ещё нет — CLI сам проверит модель при запуске
  const m = catalog.models.find((x) => x.id === choice.model);
  if (!m) return `Модели ${choice.model} нет в списке Claude Code`;
  if (choice.effort === null) return null;
  if (!m.efforts.includes(choice.effort))
    return `${m.label} не поддерживает глубину «${effortLabel(choice.effort)}»`;
  return null;
}

// ─── лимиты подписки (get_usage) ────────────────────────────────────────
export interface UsageWindow {
  /** five_hour | seven_day | seven_day_<модель> — как rateLimitType в rate_limit_event. */
  kind: string;
  /** Доля 0..1 (может быть больше 1). */
  utilization: number;
  resetsAt: string | null;
  /** normal | warning | … — как оценивает сам CLI. */
  severity: string | null;
  /** Название модели для окна по модели («Fable»). */
  model: string | null;
}

interface CliUsageLimit {
  kind?: string;
  percent?: number;
  severity?: string;
  resets_at?: string | null;
  scope?: { model?: { display_name?: string | null } | null } | null;
}

/** Окна лимитов из ответа get_usage (docs/CLI-PROTOCOL.md §11.2). Схема помечена CLI как экспериментальная. */
export function parseUsage(resp: unknown): UsageWindow[] {
  const r = (resp as { rate_limits?: { limits?: CliUsageLimit[] } & Record<string, unknown> })?.rate_limits;
  if (!r) return [];
  const out: UsageWindow[] = [];
  for (const l of r.limits ?? []) {
    if (typeof l.percent !== 'number') continue;
    const model = l.scope?.model?.display_name ?? null;
    const kind =
      l.kind === 'session'
        ? 'five_hour'
        : l.kind === 'weekly_all'
          ? 'seven_day'
          : l.kind === 'weekly_scoped' && model
            ? `seven_day_${model.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`
            : null;
    if (!kind || out.some((w) => w.kind === kind)) continue;
    out.push({
      kind,
      utilization: l.percent / 100,
      resetsAt: l.resets_at ?? null,
      severity: l.severity ?? null,
      model,
    });
  }
  // Старая форма без limits[]: окна верхнего уровня
  if (out.length === 0) {
    for (const [kind, v] of Object.entries(r)) {
      const w = v as { utilization?: number; resets_at?: string | null } | null;
      if (!w || typeof w.utilization !== 'number' || !/^(five_hour|seven_day)/.test(kind)) continue;
      out.push({
        kind,
        utilization: w.utilization / 100,
        resetsAt: w.resets_at ?? null,
        severity: null,
        model: null,
      });
    }
  }
  return out;
}

/**
 * Одно имя для одного окна лимита. rate_limit_event называет недельное окно Fable
 * `seven_day_overage_included` (CLI-PROTOCOL §11.3), а get_usage — по имени модели (`seven_day_fable`).
 */
const WINDOW_ALIASES: Record<string, string> = { seven_day_overage_included: 'seven_day_fable' };

export function normalizeWindowKind(kind: string): string {
  return WINDOW_ALIASES[kind] ?? kind;
}

/** Подпись окна лимита для интерфейса. */
export function windowLabel(kind: string, short = false): string {
  if (kind === 'overage') return short ? 'Доп. кредиты' : 'Дополнительные кредиты';
  if (kind === 'five_hour') return short ? 'Подписка, 5 ч' : 'Окно 5 часов';
  if (kind === 'seven_day') return short ? 'Неделя' : 'Неделя, все модели';
  const m = /^seven_day_(.+)$/.exec(kind);
  if (m?.[1]) {
    const name = m[1].charAt(0).toUpperCase() + m[1].slice(1);
    return short ? `${name}, неделя` : `Неделя, ${name}`;
  }
  return kind;
}
